import React, { useState, useMemo } from 'react';
import { Button, Select } from 'antd';
import {
  CalendarOutlined,
  SyncOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  FileExcelOutlined,
  MailOutlined,
  LineChartOutlined,
  FileTextOutlined,
  CloseOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './VCCReports.scss';

const { Option } = Select;

// Reusable Report Metric Card Component
const ReportMetricCard = ({ title, value, trend, subtitle, variant = 'default' }) => {
  const getTrendClass = () => {
    if (!trend) return '';
    return trend.startsWith('+') ? 'trend-positive' : 'trend-negative';
  };

  return (
    <div className={`report-metric-card metric-${variant}`}>
      <h4 className="metric-card-title">{title}</h4>
      <div className="metric-card-value">{value}</div>
      {trend && (
        <div className={`metric-card-trend ${getTrendClass()}`}>
          <span className="trend-arrow">{trend.startsWith('+') ? '↗' : '↘'}</span>
          <span className="trend-value">{trend}</span>
        </div>
      )}
      {subtitle && <p className="metric-card-subtitle">{subtitle}</p>}
    </div>
  );
};

// Reusable Summary Card Component (for different colored backgrounds)
const SummaryCard = ({ title, value, subtitle, bgColor }) => {
  return (
    <div className="summary-card" style={{ backgroundColor: bgColor }}>
      <h4 className="summary-card-title">{title}</h4>
      <div className="summary-card-value">{value}</div>
      {subtitle && <p className="summary-card-subtitle">{subtitle}</p>}
    </div>
  );
};

// Reusable Filter Row Component
const FilterRow = ({ children }) => {
  return <div className="filter-row">{children}</div>;
};

// Cell Renderer for colored numbers
const ColoredNumberRenderer = (params) => {
  const value = params.value;
  let color = '#262626';
  
  if (params.colDef.field === 'charged') {
    color = '#52c41a';
  } else if (params.colDef.field === 'pending') {
    color = '#faad14';
  } else if (params.colDef.field === 'failed') {
    color = '#ff4d4f';
  } else if (params.colDef.field === 'reconciled') {
    color = '#52c41a';
  } else if (params.colDef.field === 'cashbackEarned') {
    color = '#52c41a';
  }
  
  return <span style={{ color, fontWeight: 600 }}>{value}</span>;
};

// Status Renderer
const StatusRenderer = (params) => {
  const value = params.value;
  let className = '';
  
  if (value === 'In Progress') className = 'status-tag-warning';
  else if (value === 'Complete') className = 'status-tag-success';
  else if (value === 'Pending') className = 'status-tag-warning';
  else if (value === 'Settled') className = 'status-tag-success';
  else if (value === 'Expired') className = 'status-tag-error';
  
  return <span className={`status-tag ${className}`}>{value}</span>;
};

// Main Component
const VCCReports = () => {
  const [activeReport, setActiveReport] = useState('vcc-utilization');
  const [selectedFields, setSelectedFields] = useState(['VCC ID', 'Hotel Name', 'Amount']);

  // VCC Utilization Report Data
  const vccUtilizationData = useMemo(() => [
    { date: 'Dec 4, 2025', vccsIssued: 95, charged: 89, pending: 6, failed: 0, successRate: '93.7%', totalAmount: '₹8,45,230' },
    { date: 'Dec 3, 2025', vccsIssued: 102, charged: 97, pending: 4, failed: 1, successRate: '95.1%', totalAmount: '₹9,12,450' },
    { date: 'Dec 2, 2025', vccsIssued: 88, charged: 83, pending: 3, failed: 2, successRate: '94.3%', totalAmount: '₹7,89,670' },
    { date: 'Dec 1, 2025', vccsIssued: 76, charged: 71, pending: 5, failed: 0, successRate: '93.4%', totalAmount: '₹6,78,340' },
  ], []);

  const vccColumnDefs = useMemo(() => [
    { field: 'date', headerName: 'Date', width: 150 },
    { field: 'vccsIssued', headerName: 'VCCs Issued', width: 130 },
    { field: 'charged', headerName: 'Charged', width: 120, cellRenderer: ColoredNumberRenderer },
    { field: 'pending', headerName: 'Pending', width: 120, cellRenderer: ColoredNumberRenderer },
    { field: 'failed', headerName: 'Failed', width: 100, cellRenderer: ColoredNumberRenderer },
    { field: 'successRate', headerName: 'Success Rate', width: 140 },
    { field: 'totalAmount', headerName: 'Total Amount', width: 150 },
  ], []);

  // Daily Reconciliation Data
  const reconciliationData = useMemo(() => [
    { date: 'Dec 8, 2025', totalVCCs: 95, reconciled: 89, pending: 6, variance: '₹12,340', status: 'In Progress' },
    { date: 'Dec 7, 2025', totalVCCs: 102, reconciled: 102, pending: 0, variance: '₹0', status: 'Complete' },
    { date: 'Dec 6, 2025', totalVCCs: 88, reconciled: 88, pending: 0, variance: '₹0', status: 'Complete' },
    { date: 'Dec 5, 2025', totalVCCs: 91, reconciled: 91, pending: 0, variance: '₹0', status: 'Complete' },
  ], []);

  const reconciliationColumnDefs = useMemo(() => [
    { field: 'date', headerName: 'Date', width: 150 },
    { field: 'totalVCCs', headerName: 'Total VCCs', width: 130 },
    { field: 'reconciled', headerName: 'Reconciled', width: 130, cellRenderer: ColoredNumberRenderer },
    { field: 'pending', headerName: 'Pending', width: 120, cellRenderer: ColoredNumberRenderer },
    { field: 'variance', headerName: 'Variance', width: 130 },
    { field: 'status', headerName: 'Status', width: 140, cellRenderer: StatusRenderer },
  ], []);

  // Cashback Earnings Data
  const cashbackData = useMemo(() => [
    { month: 'November 2025', transactions: 2847, transactionValue: '₹2,45,67,890', cashbackEarned: '₹1,24,680', cashbackRate: '0.51%', settlementStatus: 'Pending' },
    { month: 'October 2025', transactions: 2654, transactionValue: '₹2,28,45,670', cashbackEarned: '₹1,15,230', cashbackRate: '0.50%', settlementStatus: 'Settled' },
  ], []);

  const cashbackColumnDefs = useMemo(() => [
    { field: 'month', headerName: 'Month', width: 180 },
    { field: 'transactions', headerName: 'Transactions', width: 140 },
    { field: 'transactionValue', headerName: 'Transaction Value', width: 170 },
    { field: 'cashbackEarned', headerName: 'Cashback Earned', width: 170, cellRenderer: ColoredNumberRenderer },
    { field: 'cashbackRate', headerName: 'Cashback Rate', width: 150 },
    { field: 'settlementStatus', headerName: 'Settlement Status', width: 160, cellRenderer: StatusRenderer },
  ], []);

  // Vendor Payment Data
  const vendorData = useMemo(() => [
    { vendorName: 'Taj Hotels', hotelChain: 'IHCL', transactions: 156, totalAmount: '₹45,67,890', avgTransaction: '₹2,92,870', lastPayment: 'Dec 3, 2025' },
    { vendorName: 'Marriott International', hotelChain: 'Marriott', transactions: 134, totalAmount: '₹38,92,450', avgTransaction: '₹2,90,480', lastPayment: 'Dec 4, 2025' },
    { vendorName: 'ITC Hotels', hotelChain: 'ITC', transactions: 98, totalAmount: '₹28,45,670', avgTransaction: '₹2,90,370', lastPayment: 'Dec 2, 2025' },
  ], []);

  const vendorColumnDefs = useMemo(() => [
    { field: 'vendorName', headerName: 'Vendor Name', width: 200 },
    { field: 'hotelChain', headerName: 'Hotel Chain', width: 150 },
    { field: 'transactions', headerName: 'Transactions', width: 140 },
    { field: 'totalAmount', headerName: 'Total Amount', width: 160 },
    { field: 'avgTransaction', headerName: 'Avg. Transaction', width: 160 },
    { field: 'lastPayment', headerName: 'Last Payment', width: 150 },
  ], []);

  // Breakage Report Data
  const breakageData = useMemo(() => [
    { vccId: 'VCC-2025-2745', hotel: 'Taj Palace Delhi', createdDate: 'Oct 15, 2025', expiryDate: 'Nov 30, 2025', amount: '₹8,450', reason: 'Booking Cancelled', status: 'Expired' },
    { vccId: 'VCC-2025-2698', hotel: 'Marriott Mumbai', createdDate: 'Oct 12, 2025', expiryDate: 'Nov 28, 2025', amount: '₹5,280', reason: 'Not Used', status: 'Expired' },
  ], []);

  const breakageColumnDefs = useMemo(() => [
    { field: 'vccId', headerName: 'VCC ID', width: 160, cellRenderer: (params) => <a className="vcc-link">{params.value}</a> },
    { field: 'hotel', headerName: 'Hotel', width: 180 },
    { field: 'createdDate', headerName: 'Created Date', width: 140 },
    { field: 'expiryDate', headerName: 'Expiry Date', width: 140 },
    { field: 'amount', headerName: 'Amount', width: 120 },
    { field: 'reason', headerName: 'Reason', width: 180 },
    { field: 'status', headerName: 'Status', width: 120, cellRenderer: StatusRenderer },
  ], []);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
  }), []);

  const availableFields = ['VCC ID', 'Hotel Name', 'Amount', 'Status', 'Created Date', 'Expiry Date', 'Charge Date', 'Guest Name', 'Booking Reference'];

  const renderReportContent = () => {
    switch (activeReport) {
      case 'vcc-utilization':
        return (
          <>
            <div className="report-metrics-grid">
              <ReportMetricCard title="Total VCCs Issued" value="2,847" trend="+12.5%" />
              <ReportMetricCard title="Successfully Charged" value="2,681" trend="+8.2%" />
              <ReportMetricCard title="Success Rate" value="94.2%" trend="+2.1%" />
              <ReportMetricCard title="Pending Charges" value="166" trend="-5.3%" variant="warning" />
            </div>
            <div className="report-table">
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                <AgGridReact rowData={vccUtilizationData} columnDefs={vccColumnDefs} defaultColDef={defaultColDef} />
              </div>
            </div>
          </>
        );

      case 'daily-reconciliation':
        return (
          <>
            <div className="summary-cards-row">
              <SummaryCard title="Fully Reconciled" value="2,456" subtitle="₹2,18,45,670" bgColor="#e6f9ed" />
              <SummaryCard title="Partially Reconciled" value="89" subtitle="₹8,45,230" bgColor="#fffbe6" />
              <SummaryCard title="Not Reconciled" value="45" subtitle="₹4,12,890" bgColor="#fff1f0" />
            </div>
            <div className="report-table">
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                <AgGridReact rowData={reconciliationData} columnDefs={reconciliationColumnDefs} defaultColDef={defaultColDef} />
              </div>
            </div>
          </>
        );

      case 'cashback-earnings':
        return (
          <>
            <div className="report-metrics-grid cashback-grid">
              <ReportMetricCard title="Total Cashback Earned" value="₹1,24,680" trend="+18.5% vs last month" variant="info" />
              <ReportMetricCard title="Upcoming Potential" value="₹7,125" subtitle="Pending charges" variant="warning" />
              <ReportMetricCard title="Avg. Cashback Rate" value="0.51%" subtitle="Of total transactions" variant="default" />
              <ReportMetricCard title="Last Settlement" value="Nov 28" subtitle="₹67,890 credited" variant="default" />
            </div>
            <div className="report-table">
              <div className="ag-theme-alpine" style={{ height: '300px', width: '100%' }}>
                <AgGridReact rowData={cashbackData} columnDefs={cashbackColumnDefs} defaultColDef={defaultColDef} />
              </div>
            </div>
          </>
        );

      case 'vendor-payment':
        return (
          <>
            <div className="report-metrics-grid vendor-grid">
              <ReportMetricCard title="Total Vendors" value="1,245" />
              <ReportMetricCard title="Total Payments" value="₹2,45,67,890" />
              <ReportMetricCard title="Avg. Payment" value="₹1,97,230" />
            </div>
            <div className="report-table">
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                <AgGridReact rowData={vendorData} columnDefs={vendorColumnDefs} defaultColDef={defaultColDef} />
              </div>
            </div>
          </>
        );

      case 'breakage-report':
        return (
          <>
            <div className="summary-cards-row breakage-cards">
              <SummaryCard title="Expired VCCs" value="18" subtitle="₹1,17,600 locked" bgColor="#fff1f0" />
              <SummaryCard title="Expiring Soon" value="12" subtitle="Next 7 days" bgColor="#fffbe6" />
              <SummaryCard title="Avg. Expiry Loss" value="₹6,533" subtitle="Per expired VCC" bgColor="#fffef0" />
            </div>
            <div className="report-table">
              <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                <AgGridReact rowData={breakageData} columnDefs={breakageColumnDefs} defaultColDef={defaultColDef} />
              </div>
            </div>
          </>
        );

      case 'custom-builder':
        return (
          <div className="custom-builder-section">
            <div className="builder-columns">
              <div className="builder-column">
                <h3 className="builder-column-title">Select Fields</h3>
                <div className="field-list">
                  {availableFields.map((field) => (
                    <div
                      key={field}
                      className={`field-item ${selectedFields.includes(field) ? 'selected' : ''}`}
                      onClick={() => {
                        if (selectedFields.includes(field)) {
                          setSelectedFields(selectedFields.filter(f => f !== field));
                        } else {
                          setSelectedFields([...selectedFields, field]);
                        }
                      }}
                    >
                      {field}
                      {selectedFields.includes(field) && <CloseOutlined className="remove-icon" />}
                    </div>
                  ))}
                </div>
                <div className="selected-count">{selectedFields.length} fields selected</div>
              </div>
              <div className="builder-column">
                <h3 className="builder-column-title">Add Filters</h3>
                <div className="filter-list">
                  <div className="filter-item">
                    <span>Date Range</span>
                    <PlusOutlined />
                  </div>
                  <div className="filter-item">
                    <span>Status</span>
                    <PlusOutlined />
                  </div>
                  <div className="filter-item">
                    <span>Amount Range</span>
                    <PlusOutlined />
                  </div>
                  <div className="filter-item">
                    <span>Hotel Chain</span>
                    <PlusOutlined />
                  </div>
                  <div className="filter-item">
                    <span>Payment Mode</span>
                    <PlusOutlined />
                  </div>
                  <div className="filter-item">
                    <span>Reconciliation Status</span>
                    <PlusOutlined />
                  </div>
                </div>
                <div className="filters-applied">0 filters applied</div>
              </div>
            </div>
            <div className="builder-actions">
              <Button size="large">Save Template</Button>
              <Button type="primary" size="large">Generate Report</Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getReportTitle = () => {
    switch (activeReport) {
      case 'vcc-utilization': return 'VCC Utilization Report';
      case 'daily-reconciliation': return 'Daily Reconciliation Summary';
      case 'cashback-earnings': return 'Cashback Earnings Report';
      case 'vendor-payment': return 'Vendor Payment Analysis';
      case 'breakage-report': return 'Breakage Report';
      case 'custom-builder': return 'Custom Report Builder';
      default: return '';
    }
  };

  const getReportSubtitle = () => {
    switch (activeReport) {
      case 'vcc-utilization': return 'Track VCC issuance, usage, and success rates';
      case 'daily-reconciliation': return 'Real-time daily reconciliation status and variances';
      case 'cashback-earnings': return 'Track cashback earned across all transactions';
      case 'vendor-payment': return 'Payment breakdown by hotel and vendor';
      case 'breakage-report': return 'Track expired VCCs and potential losses';
      case 'custom-builder': return 'Build custom reports with advanced filters';
      default: return '';
    }
  };

  return (
    <div className="reports-analytics-main-wrapper">
      <div className="reports-analytics-main-container">
        {/* Header */}
        <div className="main-reports-header">
          <div>
            <h1 className="main-reports-title">Reports & Analytics</h1>
            <p className="main-reports-subtitle">Generate and export comprehensive reports</p>
          </div>
          <div className="main-reports-actions">
            <Button icon={<CalendarOutlined />} size="large">Schedule Report</Button>
            <Button type="primary" icon={<SyncOutlined />} size="large">Refresh</Button>
          </div>
        </div>

        {/* Main Layout */}
        <div className="reports-main-layout">
          {/* Sidebar */}
          <div className="reports-main-sidebar">
            <h3 className="sidebar-main-title">Report Library</h3>
            <div className="report-items-list">
              <div className={`report-main-item ${activeReport === 'vcc-utilization' ? 'active' : ''}`} onClick={() => setActiveReport('vcc-utilization')}>
                <LineChartOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>VCC Utilization Report</h4>
                  <p>Track VCC issuance, usage, and success rates</p>
                </div>
              </div>
              <div className={`report-main-item ${activeReport === 'daily-reconciliation' ? 'active' : ''}`} onClick={() => setActiveReport('daily-reconciliation')}>
                <FileTextOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>Daily Reconciliation Summary</h4>
                  <p>Real-time daily reconciliation status and variances</p>
                </div>
              </div>
              <div className={`report-main-item ${activeReport === 'cashback-earnings' ? 'active' : ''}`} onClick={() => setActiveReport('cashback-earnings')}>
                <FileTextOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>Cashback Earnings Report</h4>
                  <p>Track cashback earned across all transactions</p>
                </div>
              </div>
              <div className={`report-main-item ${activeReport === 'vendor-payment' ? 'active' : ''}`} onClick={() => setActiveReport('vendor-payment')}>
                <FileTextOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>Vendor Payment Analysis</h4>
                  <p>Payment breakdown by hotel and vendor</p>
                </div>
              </div>
              <div className={`report-main-item ${activeReport === 'breakage-report' ? 'active' : ''}`} onClick={() => setActiveReport('breakage-report')}>
                <FileTextOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>Breakage Report</h4>
                  <p>Track expired VCCs and potential losses</p>
                </div>
              </div>
              <div className={`report-main-item ${activeReport === 'custom-builder' ? 'active' : ''}`} onClick={() => setActiveReport('custom-builder')}>
                <FileTextOutlined className="report-main-icon" />
                <div className="report-main-content">
                  <h4>Custom Report Builder</h4>
                  <p>Build custom reports with advanced filters</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="reports-main-content">
            <div className="report-content-header">
              <div>
                <h2 className="report-main-content-title">{getReportTitle()}</h2>
                <p className="report-main-content-subtitle">{getReportSubtitle()}</p>
              </div>
              {activeReport !== 'custom-builder' && (
                <div className="report-export-actions">
                  <Button icon={<FileExcelOutlined />} size="large">Excel</Button>
                  <Button icon={<FilePdfOutlined />} size="large">PDF</Button>
                  <Button icon={<DownloadOutlined />} size="large">CSV</Button>
                  <Button type="primary" icon={<MailOutlined />} size="large">Email</Button>
                </div>
              )}
            </div>

            {activeReport !== 'custom-builder' && (
              <FilterRow>
                <div className="filter-item-main">
                  <label>Date Range</label>
                  <Select defaultValue="last30days" size="large" style={{ width: '100%' }}>
                    <Option value="last7days">Last 7 Days</Option>
                    <Option value="last30days">Last 30 Days</Option>
                    <Option value="last90days">Last 90 Days</Option>
                  </Select>
                </div>
                <div className="filter-item-main">
                  <label>Group By</label>
                  <Select defaultValue="day" size="large" style={{ width: '100%' }}>
                    <Option value="day">Day</Option>
                    <Option value="week">Week</Option>
                    <Option value="month">Month</Option>
                  </Select>
                </div>
                <div className="filter-item-main">
                  <label>Filter by Status</label>
                  <Select defaultValue="all" size="large" style={{ width: '100%' }}>
                    <Option value="all">All Statuses</Option>
                    <Option value="active">Active</Option>
                    <Option value="completed">Completed</Option>
                  </Select>
                </div>
                <div className="filter-item-main">
                  <label>Compare With</label>
                  <Select defaultValue="none" size="large" style={{ width: '100%' }}>
                    <Option value="none">None</Option>
                    <Option value="previous">Previous Period</Option>
                    <Option value="lastYear">Last Year</Option>
                  </Select>
                </div>
              </FilterRow>
            )}

            <div className="report-main-section">
              <h3 className="section-main-title">{getReportTitle()}</h3>
              {renderReportContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VCCReports;